import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import { QuestionType } from 'src/app/app.types';
import {
  sortData,
  SortDirection,
  sortQuestionBy,
} from '../../../exam/result/helpers';
import { QuestionModel } from 'src/app/api/models';

import { MatIcon } from '@angular/material/icon';
import { CustomChipComponent } from '../../../../shared/components/custom-chip/custom-chip.component';
import { TranslateModule } from '@ngx-translate/core';
import { NgScrollbar } from 'ngx-scrollbar';
import { LIST_ITEM_HEIGHT } from 'src/app/utility/app.utils';
import { DynamicHeightDirective } from '../../dynamic-height.directive';

export type QuestionsOverview = QuestionModel & {
  srNo?: number;
  correctPercentage?: number;
  openQuestionAnsweredCount?: number;
  num_correct_answers: number;
  num_incorrect_answer: number;
  num_unanswered?: number;
  total_result: number;
  total_num_answers?: number;
};

@Component({
  selector: 'qa-question-overview-list',
  templateUrl: './question-overview-list.component.html',
  styleUrls: ['./question-overview-list.component.scss'],
  imports: [
    MatIcon,
    CustomChipComponent,
    TranslateModule,
    NgScrollbar,
    DynamicHeightDirective,
  ],
})
export class QuestionOverviewListComponent implements OnChanges {
  @Input() questions: QuestionsOverview[];
  @Input() isLiveEvent = false;
  @Input() hideUnAnswered = false;
  @Output() selectQuestionResult = new EventEmitter<{
    result: QuestionsOverview;
    index: number;
  }>();
  questionType = QuestionType;
  LIST_ITEM_HEIGHT = LIST_ITEM_HEIGHT;
  loading = false;
  sortDir: string;
  currentSorting;
  allQuestions: QuestionsOverview[];
  currentSort = {
    column: '' as keyof QuestionsOverview,
    direction: '' as SortDirection,
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.questions && changes?.questions?.currentValue) {
      this.updateQuestions();
      if (!this.allQuestions && this.questions?.length) {
        this.allQuestions = JSON.parse(JSON.stringify(this.questions));
      }
    }
  }

  updateQuestions() {
    this.questions.forEach((q, i) => {
      q.srNo = i + 1;
      q.num_correct_answers =
        q.is_poll_question || q.type === QuestionType.FREETEXT
          ? 0
          : q?.num_correct_answers;
      q.correctPercentage =
        q.is_poll_question || q.type === QuestionType.FREETEXT
          ? 0
          : Number(this.getCorrectRatio(q));
    });
  }

  getQuestionResult(result: QuestionsOverview, questionIndex) {
    this.selectQuestionResult.emit({
      result,
      index: questionIndex,
    });
  }

  getCorrectRatio(stat: QuestionsOverview) {
    return stat && stat.total_result > 0
      ? `${((stat.num_correct_answers * 100) / stat.total_result).toFixed(2)}`
      : 0;
  }

  getRightResult(question: QuestionsOverview) {
    const total =
      question.num_correct_answers +
      question.num_incorrect_answer +
      question.num_unanswered;
    const quotient = question.num_correct_answers / total || 0;
    return `${Math.round(quotient * 100)}%`;
  }

  getWrongResult(question: QuestionsOverview) {
    const total =
      question.num_correct_answers +
      question.num_incorrect_answer +
      question.num_unanswered;
    const quotient = question.num_incorrect_answer / total || 0;
    return `${Math.round(quotient * 100)}%`;
  }

  sort(sortBy: string) {
    if (this.currentSorting !== sortBy) {
      this.sortDir = null;
    }
    this.sortDir = !this.sortDir
      ? 'ASC'
      : this.sortDir === 'ASC'
      ? 'DESC'
      : null;
    if (this.sortDir) {
      this.sortQuestions(sortBy, this.sortDir);
    } else {
      this.questions = JSON.parse(JSON.stringify(this.allQuestions));
    }
  }

  sortQuestions(sortby = 'alphabetical', sortDir?: string): void {
    this.currentSorting = sortby;
    this.questions = sortQuestionBy(this.questions, sortby, sortDir);
  }

  toggleSort(column: keyof QuestionsOverview) {
    const current = this.currentSort.direction;
    const next: SortDirection = !current
      ? 'asc'
      : current === 'asc'
      ? 'desc'
      : '';
    this.currentSort = {
      column: next ? column : null,
      direction: next,
    };
    this.questions = sortData(this.allQuestions, column, next);
  }
}
